<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="handleDialogClose">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-40 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">

              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10"
                       :class="hasError ? 'bg-red-100' : (eligibleCount === 0 ? 'bg-blue-100' : 'bg-yellow-100')">
                    <Icon :name="hasError ? 'heroicons:x-circle' : (eligibleCount === 0 ? 'heroicons:information-circle' : 'heroicons:exclamation-triangle')"
                          :class="hasError ? 'h-6 w-6 text-red-600' : (eligibleCount === 0 ? 'h-6 w-6 text-blue-600' : 'h-6 w-6 text-yellow-600')" />
                  </div>
                  <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                      {{ hasError ? 'Unable to Check Students' : (eligibleCount === 0 ? 'No Eligible Students' : 'Eligible Students Found') }}
                    </DialogTitle>
                    <div class="mt-2">
                      <div v-if="loading" class="flex items-center justify-center py-4">
                        <LoadingCircle size="8" />
                        <span class="ml-2 text-gray-500">Checking eligible students...</span>
                      </div>
                      <div v-else-if="hasError" class="py-4">
                        <div class="flex items-center p-3 bg-red-50 rounded-md">
                          <Icon name="heroicons:exclamation-circle" class="h-5 w-5 text-red-500 mr-2" />
                          <p class="text-sm text-red-700">{{ errorMessage }}</p>
                        </div>
                        <p class="text-sm text-gray-500 mt-3">
                          The fee has been saved successfully. You can close this dialog and continue.
                        </p>
                      </div>
                      <div v-else-if="eligibleCount === 0">
                        <p class="text-sm text-gray-500">
                          No students are currently available in this club to connect with this fee.
                        </p>
                        <p class="text-sm text-gray-500 mt-3">
                          The fee has been saved successfully. You can add students to this club later and connect them to this fee.
                        </p>

                        <!-- Fee Info -->
                        <div class="mt-4 p-3 bg-gray-50 rounded-md">
                          <p class="text-sm font-medium text-gray-700">Fee: {{ feeInfo?.name }}</p>
                          <p class="text-sm text-gray-500">Amount: ${{ feeInfo?.amount?.toFixed(2) }}</p>
                          <p class="text-sm text-gray-500">Type: {{ feeInfo?.type === 'RECURRING' ? 'Recurring' : 'One-time' }}</p>
                        </div>
                      </div>
                      <div v-else>
                        <p class="text-sm text-gray-500">
                          <span class="font-semibold text-brand-color">{{ eligibleCount }}</span> eligible student(s) in this program match the fee criteria.
                        </p>
                        <p class="text-sm text-gray-500 mt-3">
                          Would you like to connect this new fee to them?
                        </p>
                        <p class="text-sm text-gray-400 mt-2 italic">
                          If you do, invoices will be created for these students based on this fee's invoice date.
                        </p>

                        <!-- Fee Info -->
                        <div class="mt-4 p-3 bg-gray-50 rounded-md">
                          <p class="text-sm font-medium text-gray-700">Fee: {{ feeInfo?.name }}</p>
                          <p class="text-sm text-gray-500">Amount: ${{ feeInfo?.amount?.toFixed(2) }}</p>
                          <p class="text-sm text-gray-500">Type: {{ feeInfo?.type === 'RECURRING' ? 'Recurring' : 'One-time' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  v-if="!hasError && eligibleCount > 0"
                  type="button"
                  :disabled="loading || processing"
                  @click="onProceed"
                  class="inline-flex w-full justify-center rounded-md bg-brand-color px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-color-800 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
                  <LoadingCircle v-if="processing" size="5" class="mr-2" />
                  {{ processing ? 'Connecting...' : 'Proceed' }}
                </button>
                <button
                  type="button"
                  :disabled="processing"
                  @click="onSkip"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50"
                  :class="{ 'sm:ml-3': hasError || eligibleCount === 0 }">
                  {{ hasError || eligibleCount === 0 ? 'Close' : 'No, skip the students' }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  eligibleStudentsWarningDialogue,
  eligibleStudentsDialogueClosedEvent,
  useGlobalEvent,
  useGlobalEventLister,
  useNotificationEvent,
  feePageRefreshEvent
} from "~/composables/useEventBus";
import { feeStore } from "~/store/feeStore";
import { companyStore } from "~/store/companyStore";
import { userStore } from "~/store/userStore";
import { storeToRefs } from "pinia";
import type { NotificationDetails } from "~/models/notificationDetails";
import { NotificationType } from "~/utils/enums/NotificationType";

const useFeeStore = feeStore();
const useCompanyStore = companyStore();
const useUserStore = userStore();
const { getCompanyId } = storeToRefs(useCompanyStore);
const { getLoggedInUser } = storeToRefs(useUserStore);

const open = ref(false);
const loading = ref(false);
const processing = ref(false);
const eligibleCount = ref(0);
const eligibleStudents = ref<any[]>([]);
const feeInfo = ref<any>(null);
const clubId = ref<number | null>(null);
const clubFeeId = ref<string | null>(null);
const redirectPath = ref<string | null>(null);
const sourceContext = ref<string | null>(null);
const hasError = ref(false);
const errorMessage = ref<string>('');

const onProceed = async () => {
  if (eligibleStudents.value.length === 0) {
    onSkip();
    return;
  }

  processing.value = true;
  try {
    const studentIds = eligibleStudents.value.map(s => s.id);

    const response = await useFeeStore.connectFeeToStudents({
      clubId: clubId.value!,
      clubFeeId: clubFeeId.value!,
      studentIds: studentIds,
      companyId: getCompanyId.value,
      createInvoices: true,
      createdBy: getLoggedInUser.value.id
    });

    if (response.IsOperationSuccessful) {
      const emailsMsg = response.Result?.emailsSent > 0 ? ` ${response.Result.emailsSent} email(s) sent.` : '';
      useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
        type: NotificationType.SUCCESS,
        title: 'Success',
        message: `Fee connected to ${response.Result?.connectedCount || studentIds.length} student(s). ${response.Result?.invoicesCreated || 0} invoice(s) created.${emailsMsg}`,
      });
      useGlobalEvent(feePageRefreshEvent(), {});
    } else {
      useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
        type: NotificationType.ERROR,
        title: 'Error',
        message: response.Message || 'Failed to connect fee to students',
      });
    }
  } catch (error) {
    console.error('Error connecting fee to students:', error);
    useGlobalEvent(useNotificationEvent(), <NotificationDetails>{
      type: NotificationType.ERROR,
      title: 'Error',
      message: 'An error occurred while connecting the fee to students',
    });
  } finally {
    processing.value = false;
    closeDialog();
  }
};

const onSkip = () => {
  if (processing.value) return;
  closeDialog();
};

const handleDialogClose = () => {
  // Prevent closing while loading or processing
  if (loading.value || processing.value) return;
  // Allow closing via outside click only when there are eligible students (user can skip)
  // For error or no students, user must click the button
  if (eligibleCount.value > 0 && !hasError.value) {
    closeDialog();
  }
};

const closeDialog = () => {
  open.value = false;

  // Emit closed event with redirect info
  if (redirectPath.value || sourceContext.value) {
    useGlobalEvent(eligibleStudentsDialogueClosedEvent(), {
      redirectPath: redirectPath.value,
      sourceContext: sourceContext.value
    });
  }

  // Reset state
  eligibleCount.value = 0;
  eligibleStudents.value = [];
  feeInfo.value = null;
  clubId.value = null;
  clubFeeId.value = null;
  redirectPath.value = null;
  sourceContext.value = null;
  hasError.value = false;
  errorMessage.value = '';
};

const fetchEligibleStudents = async (cId: number, cfId: string) => {
  loading.value = true;
  hasError.value = false;
  errorMessage.value = '';
  try {
    const response = await useFeeStore.getEligibleStudentsForFee({
      clubId: cId,
      clubFeeId: cfId,
      companyId: getCompanyId.value
    });

    if (response.IsOperationSuccessful && response.Result) {
      eligibleCount.value = response.Result.eligibleCount || 0;
      eligibleStudents.value = response.Result.students || [];
      feeInfo.value = response.Result.feeInfo || null;
    } else {
      hasError.value = true;
      errorMessage.value = response.Message || 'Failed to fetch eligible students';
    }
  } catch (error: any) {
    console.error('Error fetching eligible students:', error);
    hasError.value = true;
    if (error?.response?.status === 404) {
      errorMessage.value = 'The eligible students API is not available yet. Please try again later.';
    } else {
      errorMessage.value = error?.message || 'An error occurred while fetching eligible students';
    }
  } finally {
    loading.value = false;
  }
};

function setupEventListener() {
  useGlobalEventLister(eligibleStudentsWarningDialogue(), async function (data: any) {
    console.log('EligibleStudentsWarningDialogue received event:', data);
    if (data && data.clubId && data.clubFeeId) {
      clubId.value = data.clubId;
      clubFeeId.value = data.clubFeeId;
      redirectPath.value = data.redirectPath || null;
      sourceContext.value = data.sourceContext || null;
      open.value = true;
      console.log('Dialog opened, fetching eligible students...');
      await fetchEligibleStudents(data.clubId, data.clubFeeId);
      console.log('Eligible students fetched. Count:', eligibleCount.value, 'hasError:', hasError.value);
    }
  });
}

onMounted(() => {
  setupEventListener();
});
</script>
